import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import { connectSocket } from '../../service/socket';

/**
 * Popup modal that fires whenever the server emits `leadAssigned` to the
 * connected user. Mounts once at the dashboard-layout level. Includes a
 * live 5-minute countdown so the LO can see how long they have before
 * the lead enters the shark tank.
 *
 * Multiple assignments in quick succession queue up — only the most
 * recent is shown; older ones are still in the bell/notification list.
 */
const LeadAssignedPopup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [event, setEvent] = useState(null);
  const [now, setNow] = useState(Date.now());
  const tickRef = useRef(null);

  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return undefined;

    const handler = (payload) => {
      setEvent(payload);
      setOpen(true);
    };
    socket.on('leadAssigned', handler);
    return () => {
      socket.off('leadAssigned', handler);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, [open]);

  const close = () => setOpen(false);

  const goToLead = () => {
    if (event?.link) navigate(event.link);
    close();
  };

  if (!event) return null;

  const SLA_MS = 5 * 60 * 1000;
  const assignedAtMs = event.assignedAt ? new Date(event.assignedAt).getTime() : now;
  const elapsed = Math.max(0, now - assignedAtMs);
  const remaining = Math.max(0, SLA_MS - elapsed);
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  const pct = Math.min(100, (elapsed / SLA_MS) * 100);
  const expired = remaining === 0;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <NotificationsActiveIcon color="primary" />
          <span>New Bankrate lead assigned to you</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Borrower
            </Typography>
            <Typography variant="h6">{event.borrowerName || '—'}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={`State: ${event.propertyState || '—'}`} size="small" />
            {event.reason === 'shark_tank_claim' && (
              <Chip label="Shark-tank claim" color="warning" size="small" />
            )}
          </Stack>
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Time to make first contact
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={expired ? 'error.main' : 'text.primary'}
              >
                {expired
                  ? 'SLA expired — lead in shark tank'
                  : `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={expired ? 'error' : pct > 60 ? 'warning' : 'primary'}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Dismiss</Button>
        <Button onClick={goToLead} variant="contained">
          Open lead
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadAssignedPopup;
